import React, { useState, useEffect, useCallback } from 'react';
import { X as XIcon, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import contactService from '../../services/contactService';
import { EMAIL_RE } from './GhostCard.jsx';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

const notifyContactChanged = () => {
    try { window.dispatchEvent(new CustomEvent('omnis:contact-changed')); } catch (_) { /* ignore */ }
};

const ModalShell = ({ title, testid, onClose, children }) => {
    const { t } = useLanguage();
    return (
    <Modal onClose={onClose} zIndex="z-[85]" layout="column" cardClassName="max-h-[85dvh]" cardTestId={testid}>
            <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                <h3 className="text-base font-semibold text-white">{title}</h3>
                <button onClick={onClose} aria-label={t('commonClose', 'Close')} className="text-pai-faint hover:text-white p-1 transition-colors">
                    <XIcon size={16} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {children}
            </div>
        </Modal>
    );
};

// ── Pending Requests portal (FE-7) ─────────────────────────────────────
export const PendingPortal = ({ onClose, onChanged }) => {
    const { t } = useLanguage();
    const [received, setReceived] = useState([]);
    const [sent, setSent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await contactService.listRequests();
            setReceived(res.received || []);
            setSent(res.sent || []);
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const respond = async (req, decision) => {
        setBusyId(req.id);
        setError(null);
        try {
            await contactService.respondRequest(req.id, decision);
            notifyContactChanged();
            onChanged?.();
            await load();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusyId(null);
        }
    };

    const cancel = async (req) => {
        setBusyId(req.id);
        setError(null);
        try {
            await contactService.cancelRequest(req.id);
            notifyContactChanged();
            onChanged?.();
            await load();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <ModalShell title={t('pendingTitle')} testid="pending-portal" onClose={onClose}>
            {error && <div className="text-xs text-pai-red">{error}</div>}
            {loading ? (
                <div className="flex items-center justify-center py-6 text-pai-faint"><Loader2 size={18} className="animate-spin" /></div>
            ) : (
                <>
                    <div>
                        <div className="text-[11px] uppercase tracking-wide font-semibold text-pai-meta mb-2">
                            {t('pendingReceivedHeading')}
                        </div>
                        {received.length === 0 ? (
                            <p className="text-xs text-pai-faint italic">{t('pendingNoReceived')}</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {received.map((r) => (
                                    <li key={r.id} data-testid="pending-received-row" className="flex items-center gap-2 bg-white/[0.025] rounded-chip border border-pai-subtle px-2 py-1.5">
                                        <span className="flex-1 min-w-0 text-xs text-pai-body truncate">{r.email}</span>
                                        <button
                                            data-testid="pending-accept"
                                            onClick={() => respond(r, 'accept')}
                                            disabled={busyId === r.id}
                                            className="px-2.5 py-1 text-[11px] font-semibold text-pai-green border border-pai-green/40 bg-pai-green/[0.1] hover:bg-pai-green/[0.18] disabled:opacity-50 rounded-chip transition-colors"
                                        >
                                            {t('pendingAcceptBtn')}
                                        </button>
                                        <button
                                            data-testid="pending-decline"
                                            onClick={() => respond(r, 'decline')}
                                            disabled={busyId === r.id}
                                            className="px-2 py-1 text-[11px] font-medium text-pai-secondary bg-white/[0.06] hover:bg-white/[0.09] rounded-chip"
                                        >
                                            {t('pendingDeclineBtn')}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-wide font-semibold text-pai-meta mb-2">
                            {t('pendingSentHeading')}
                        </div>
                        {sent.length === 0 ? (
                            <p className="text-xs text-pai-faint italic">{t('pendingNoSent')}</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {sent.map((s) => (
                                    <li key={s.id} data-testid="pending-sent-row" className="flex items-center gap-2 bg-white/[0.025] rounded-chip border border-pai-subtle px-2 py-1.5">
                                        <span className="flex-1 min-w-0 text-xs text-pai-body truncate">{s.email}</span>
                                        <span className="text-[10px] text-pai-meta">{t('pendingStatusLabel')}</span>
                                        <button
                                            data-testid="pending-cancel"
                                            onClick={() => cancel(s)}
                                            disabled={busyId === s.id}
                                            className="px-2.5 py-1 text-[11px] font-semibold text-pai-red hover:bg-pai-red/[0.12] rounded-chip transition-colors"
                                        >
                                            {t('pendingCancelBtn')}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </ModalShell>
    );
};

// ── Block List modal (FE-7) ────────────────────────────────────────────
export const BlockListModal = ({ onClose, onChanged }) => {
    const { t } = useLanguage();
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState(null);
    const [addEmail, setAddEmail] = useState('');
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await contactService.listBlocks();
            setBlocks(res.blocks || []);
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const add = async () => {
        const email = addEmail.trim();
        if (!EMAIL_RE.test(email)) { setError(t('discoverErrGeneric')); return; }
        setAdding(true);
        setError(null);
        try {
            await contactService.block(email);
            setAddEmail('');
            notifyContactChanged();
            onChanged?.();
            await load();
        } catch (err) {
            // Surfaces the BE's flat discriminator (already_blocked / No PAI user found. / …)
            setError(userErrorMessage(err, t));
        } finally {
            setAdding(false);
        }
    };

    const unblock = async (block) => {
        setBusyId(block.id);
        setError(null);
        try {
            await contactService.unblock(block.id);
            notifyContactChanged();
            onChanged?.();
            await load();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <ModalShell title={t('blockListTitle')} testid="blocklist-modal" onClose={onClose}>
            <div className="flex items-center gap-2">
                <input
                    type="email"
                    value={addEmail}
                    data-testid="blocklist-add-email"
                    onChange={(e) => setAddEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    placeholder={t('blockAddPlaceholder')}
                    className="flex-1 px-2 py-1.5 text-sm pai-input"
                />
                <button
                    data-testid="blocklist-add-btn"
                    onClick={add}
                    disabled={adding || !addEmail.trim()}
                    className="pai-btn-danger disabled:opacity-50"
                >
                    {t('blockAddBtn')}
                </button>
            </div>
            {error && <div className="text-xs text-pai-red">{error}</div>}

            {loading ? (
                <div className="flex items-center justify-center py-6 text-pai-faint"><Loader2 size={18} className="animate-spin" /></div>
            ) : blocks.length === 0 ? (
                <p className="text-xs text-pai-faint italic">{t('blockListEmpty')}</p>
            ) : (
                <ul className="space-y-1.5">
                    {blocks.map((b) => (
                        <li key={b.id} data-testid="blocklist-row" className="flex items-center gap-2 bg-white/[0.025] rounded-chip border border-pai-subtle px-2 py-1.5">
                            <span className="flex-1 min-w-0 text-xs text-pai-body truncate">{b.email}</span>
                            <button
                                data-testid="blocklist-unblock"
                                onClick={() => unblock(b)}
                                disabled={busyId === b.id}
                                className="px-2 py-1 text-[11px] font-medium text-pai-secondary bg-white/[0.06] hover:bg-white/[0.09] rounded-chip"
                            >
                                {t('blockUnblockBtn')}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </ModalShell>
    );
};
